import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { searchBarPropTypes } from './SearchBarPropTypes';
import { KEYBOARD_KEY } from '@App/common/constants/keyboardKey';
import useEscapeKeyDown from '@App/common/components/Modal/hooks/useEscapeKeyDown';
import { debounce } from '@App/common/utils/debounce';
import Icon from '@Theme/natucare/components/Icon/Icon';
import * as styles from './SearchBar.module.scss';
const SearchBar = /*#__PURE__*/forwardRef(({
  placeholder,
  onChange,
  isMobileOpen,
  handleOpenMobile,
  setIsSearching,
  isSearching,
  classname
}, ref) => {
  useEscapeKeyDown(isSearching, () => setIsSearching(false));
  const {
    0: mobileSearchBarExpanded,
    1: setMobileSearchBarExpanded
  } = useState(false);
  const expandAndFocusSearchBar = () => {
    if (!mobileSearchBarExpanded) {
      if (isMobileOpen) {
        handleOpenMobile();
      }
      setMobileSearchBarExpanded(true);
      setIsSearching(true);
    } else {
      setMobileSearchBarExpanded(false);
      setIsSearching(false);
    }
  };
  useImperativeHandle(ref, () => ({
    expandAndFocusSearchBar
  }));
  const inputRef = useRef();
  useEffect(() => {
    if (mobileSearchBarExpanded) {
      inputRef === null || inputRef === void 0 ? void 0 : inputRef.current.focus();
    }
  }, [mobileSearchBarExpanded]);
  useEffect(() => {
    if (mobileSearchBarExpanded && !isSearching) {
      setMobileSearchBarExpanded(false);
    }
    if (!mobileSearchBarExpanded && isSearching) {
      setMobileSearchBarExpanded(true);
    }
  }, [mobileSearchBarExpanded, isSearching]);
  const handleSearch = value => {
    if (value === '') {
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    onChange(value);
  };
  const debouncedOnChange = useCallback(debounce(value => {
    handleSearch(value);
  }, 800), [onChange]);
  const handleSearchChange = e => debouncedOnChange(e.target.value.trim());
  const handleKeyDown = e => {
    if (e.key === KEYBOARD_KEY.ENTER) {
      const value = e.target.value.trim();
      handleSearch(value);
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: `${styles.root} ${mobileSearchBarExpanded ? styles.rootExpanded : ''} ${classname || ''}`
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "search",
    size: {
      width: 24,
      height: 24
    },
    onClick: expandAndFocusSearchBar,
    classname: styles.rootIcon
  }), /*#__PURE__*/React.createElement("input", {
    type: "text",
    placeholder: placeholder,
    onChange: handleSearchChange,
    onKeyDown: handleKeyDown,
    ref: inputRef,
    className: styles.rootInput
  }), isSearching && /*#__PURE__*/React.createElement(Icon, {
    name: "searchBarClose",
    size: {
      width: 24,
      height: 24
    },
    onClick: () => setIsSearching(false),
    classname: `${styles.rootIcon} ${styles.rootIconClose}`
  }));
});
SearchBar.displayName = 'SearchBar';
export default SearchBar;